import {
  Conversation,
  Block,
  MessageRole,
} from 'lms/views/CourseCreationAssistant/types/chat';
import { CourseSummary } from 'lms/views/CourseCreationAssistant/types/summary';
import { LessonType } from 'types/lessons';

export const step1Blocks: Block[] = [
  {
    type: 'markdown',
    text: '*¿Cómo describirías tu curso?*',
  },
  {
    type: 'markdown',
    text: 'Puedes contarme qué aprenderán, el propósito o a quién va dirigido. También puedes agregar enlaces o referencias si ya los tienes.',
  },
];

export const courseSummaryMock: CourseSummary = {
  id: 'ldr-transformacional-equipos-alto-rendimiento',
  title: 'Liderazgo Transformacional para Equipos de Alto Rendimiento',
  description:
    'Impulsa tu liderazgo y transforma tu equipo en una potencia de resultados. Aprende a inspirar, coordinar y escalar el impacto de forma sostenible.',
  audience:
    'Líderes de equipo, managers, coordinadores y talento en desarrollo',
  difficulty: 'Intermedio',
  tone: 'Motivacional',
  duration: 'Medio (1 a 3 horas)',
  modules: [
    {
      index: 1,
      title: 'Fundamentos del liderazgo transformacional',
      lessons: [
        {
          index: 1,
          type: LessonType.VIDEO,
          title: '¿Qué es el liderazgo transformacional?',
          description:
            'Conoce este estilo de liderazgo y cómo impulsa cambios positivos en los equipos.',
        },
        {
          index: 2,
          type: LessonType.LECTURE,
          title: 'Diferencia entre liderar y dirigir',
          description:
            'Aprende por qué liderar no es lo mismo que mandar y cómo marcar la diferencia.',
        },
        {
          index: 3,
          type: LessonType.LECTURE,
          title: 'Características clave de un líder de alto impacto',
          description:
            'Descubre las habilidades que definen a los líderes que inspiran y transforman.',
        },
        {
          index: 4,
          type: LessonType.SURVEY,
          title: 'Autodiagnóstico: tu estilo de liderazgo actual',
          description:
            'Reflexiona sobre tu forma de liderar y detecta tus principales fortalezas.',
        },
      ],
    },
    {
      index: 2,
      title: 'Construcción de equipos de alto rendimiento',
      lessons: [
        {
          index: 1,
          type: LessonType.LECTURE,
          title: 'Roles, responsabilidades y acuerdos de trabajo',
          description:
            'Define expectativas claras y reduce fricciones en la colaboración diaria.',
        },
        {
          index: 2,
          type: LessonType.VIDEO,
          title: 'Comunicación efectiva y feedback continuo',
          description:
            'Cómo instaurar conversaciones de mejora sin perder la motivación del equipo.',
        },
        {
          index: 3,
          type: LessonType.TASK,
          title: 'Rutinas de equipo y tableros de seguimiento',
          description:
            'Diseña rituales ágiles, métricas y tableros para sostener la ejecución.',
        },
        {
          index: 4,
          type: LessonType.FORUM,
          title: 'Foro: obstáculos para el alto rendimiento',
          description:
            'Comparte barreras reales y co-crea tácticas para superarlas.',
        },
      ],
    },
    {
      index: 3,
      title: 'Inspirar, alinear y ejecutar con propósito',
      lessons: [
        {
          index: 1,
          type: LessonType.LECTURE,
          title: 'Propósito, visión y OKR/OGSM',
          description:
            'Traduce el propósito en objetivos medibles y alineados entre áreas.',
        },
        {
          index: 2,
          type: LessonType.LECTURE,
          title: 'Motivación y reconocimiento inteligente',
          description:
            'Prácticas para elevar la motivación intrínseca y celebrar el progreso.',
        },
        {
          index: 3,
          type: LessonType.VIDEO,
          title: 'Reuniones efectivas y cadencias de ejecución',
          description:
            'Estructuras simples para que cada reunión agregue valor y acelere decisiones.',
        },
        {
          index: 4,
          type: LessonType.TASK,
          title: 'Mapa de alineación y dependencias',
          description:
            'Identifica responsables, riesgos y dependencias críticas para tu iniciativa.',
        },
      ],
    },
    {
      index: 4,
      title: 'Cierre, reflexión y plan de acción',
      lessons: [
        {
          index: 1,
          type: LessonType.TASK,
          title: 'Plan 30-60-90: de la intención a la práctica',
          description:
            'Estructura acciones concretas con hitos, responsables y métricas.',
        },
        {
          index: 2,
          type: LessonType.FORUM,
          title: 'Compromisos públicos del equipo',
          description:
            'Declara compromisos clave y solicita accountability entre pares.',
        },
        {
          index: 3,
          type: LessonType.EVALUATION,
          title: 'Evaluación final',
          description:
            'Verifica tus conocimientos y tu capacidad de aplicación práctica.',
        },
        {
          index: 4,
          type: LessonType.SURVEY,
          title: 'Encuesta de retroalimentación del curso',
          description:
            'Comparte tu experiencia para mejorar futuras ediciones.',
        },
      ],
    },
  ],
};

export const conversationMock: Conversation = {
  sessionId: 'session-1',
  state: {
    currentStep: 'step-1',
    nextStep: 'step-2',
  },
  messages: [
    {
      messageId: '54d350c5-0c18-433e-9fc8-0984a9fa1a6a',
      role: 'ASSISTANT',
      text: '',
      timestamp: '2025-10-02T14:32:16.336967',
      isBlock: true,
      isCurrent: false,
      isLastStep: false,
      stepId: 'a06d32ae-e525-45d6-8f64-9967a9cad9ef',
      blocks: [
        {
          text: '*¿Cómo describirías tu curso?*',
          type: 'markdown',
          value:
            '¿Te apasiona el mundo del gaming y sueñas con tener una computadora hecha a tu medida? En este curso aprenderás, paso a paso, cómo armar tu propia PC Gamer desde cero, entendiendo cada componente y su función para que no solo logres un equipo potente, sino también optimizado a tu presupuesto y necesidades. Iniciaremos con la selección de procesadores, tarjetas gráficas, memorias RAM y discos de almacenamiento, evaluando compatibilidades y rendimiento real en juegos. También veremos fuentes de poder, gabinetes, sistemas de refrigeración y la importancia del cableado para lograr un ensamble limpio y eficiente. A lo largo de las clases tendrás consejos prácticos para evitar errores comunes, recomendaciones de marcas y configuraciones equilibradas según diferentes niveles de inversión. Al finalizar, serás capaz de ensamblar tu propia PC Gamer con confianza, ahorrar dinero en servicios externos y disfrutar la satisfacción de jugar en un equipo creado por ti mismo.',
        },
        {
          text: 'Puedes contarme qué aprenderán, el propósito o a quién va dirigido. También puedes agregar enlaces o referencias si ya los tienes.',
          type: 'markdown',
        },
      ],
      metadata: {
        allow_file_input: false,
        allow_text_input: true,
      },
    },
    {
      messageId: '562ee10a-0a43-4159-8688-17e7bdac920f',
      role: 'USER',
      text: '[{"type": "markdown", "text": "\\u00bfTe apasiona el mundo del gaming y sue\\u00f1as con tener una computadora hecha a tu medida? En este curso aprender\\u00e1s, paso a paso, c\\u00f3mo armar tu propia PC Gamer desde cero, entendiendo cada componente y su funci\\u00f3n para que no solo logres un equipo potente, sino tambi\\u00e9n optimizado a tu presupuesto y necesidades."}]',
      timestamp: '2025-10-02T14:32:24.678402',
      isBlock: false,
      isCurrent: false,
      isLastStep: false,
      stepId: null,
      blocks: [],
      metadata: null,
    },
    {
      messageId: '81eb1456-32f1-4bf0-aed0-945740b5e592',
      role: 'ASSISTANT',
      text: '',
      timestamp: '2025-10-02T14:32:25.319846',
      isBlock: true,
      isCurrent: false,
      isLastStep: false,
      stepId: 'e6669895-c43f-4ed6-898d-47116c154187',
      blocks: [
        {
          text: '¡Gracias, eso suena genial! Ahora definamos el nivel de aprendizaje que tendrá tu curso.',
          type: 'markdown',
        },
        {
          text: '*¿Selecciona el nivel de dificultad?*',
          type: 'markdown',
        },
        {
          name: 'difficulty-level',
          type: 'radio-group',
          value: 'difficulty-2',
          options: [
            {
              id: 'difficulty-1',
              label: '*Principiante*',
            },
            {
              id: 'difficulty-2',
              label: '*Intermedio*',
            },
            {
              id: 'difficulty-3',
              label: '*Avanzado*',
            },
            {
              id: 'difficulty-4',
              label: '*Experto*',
            },
          ],
          required: true,
        },
      ],
      metadata: {},
    },
    {
      messageId: '0de34498-951b-4c01-ba9f-2d5eff51cfa5',
      role: 'USER',
      text: '[{"type": "markdown", "text": "*Intermedio*"}]',
      timestamp: '2025-10-02T14:32:32.012620',
      isBlock: false,
      isCurrent: false,
      isLastStep: false,
      stepId: null,
      blocks: [],
      metadata: null,
    },
    {
      messageId: '495494dc-75ee-41d9-b3fd-0fd82a236dd2',
      role: 'ASSISTANT',
      text: '',
      timestamp: '2025-10-02T14:32:32.654547',
      isBlock: true,
      isCurrent: false,
      isLastStep: false,
      stepId: 'edddfe98-0c8f-459b-999a-2fa442f9b7c9',
      blocks: [
        {
          text: '¡Perfecto! Ya tengo claro de qué trata tu curso; Ahora cuéntame, ¿A quién está dirigido? Puedes mencionar a quiénes esperas impactar con este curso. Por ejemplo:',
          type: 'markdown',
        },
        {
          text: '*¿Quién será la audiencia principal?*',
          type: 'markdown',
        },
        {
          name: 'audience',
          type: 'checkbox-group',
          value: ['audience-3'],
          options: [
            {
              id: 'audience-1',
              label: '*Nuevos colaboradores*',
            },
            {
              id: 'audience-2',
              label: '*Equipos de ventas*',
            },
            {
              id: 'audience-3',
              label: '*Líderes de Equipo*',
            },
            {
              id: 'audience-4',
              label: '*Talento Senior*',
            },
            {
              id: 'audience-5',
              label: '*Toda la organización*',
            },
            {
              id: 'skip',
              label: '*Saltar por ahora*',
            },
          ],
        },
      ],
      metadata: {},
    },
    {
      messageId: '81caca47-23b7-4230-a5c5-9c6cc04ac9c0',
      role: 'USER',
      text: '[{"type": "list", "items": ["*L\\u00edderes de Equipo*"]}]',
      timestamp: '2025-10-02T14:32:39.174551',
      isBlock: false,
      isCurrent: false,
      isLastStep: false,
      stepId: null,
      blocks: [],
      metadata: null,
    },
    {
      messageId: '8f559c6c-833d-46e6-b2f9-08d04c1b4c62',
      role: 'ASSISTANT',
      text: '',
      timestamp: '2025-10-02T14:32:39.815947',
      isBlock: true,
      isCurrent: false,
      isLastStep: false,
      stepId: '69e5ad44-3f96-42d8-b973-b7e7c68a76d5',
      blocks: [
        {
          text: 'Ahora elige el tono que quieres para tu curso. Esto nos ayudará a definir cómo se presentará el contenido, desde el lenguaje hasta los ejemplos que usaremos.',
          type: 'markdown',
        },
        {
          text: '*¿Qué estilo prefieres?*',
          type: 'markdown',
        },
        {
          name: 'course-tone',
          type: 'radio-group',
          value: 'tone-2',
          options: [
            {
              id: 'tone-1',
              label: '*Académico:* Claro, estructurado y formal.',
            },
            {
              id: 'tone-2',
              label: '*Informal:* Cercano y relajado.',
            },
            {
              id: 'tone-3',
              label: '*Motivacional:* Energético e inspirador.',
            },
            {
              id: 'skip',
              label: '*Saltar por ahora*',
            },
          ],
          required: true,
        },
      ],
      metadata: {},
    },
    {
      messageId: 'd635d642-1dfc-41f4-bb3b-25337ff58d8f',
      role: 'USER',
      text: '[{"type": "markdown", "text": "*Informal:* Cercano y relajado."}]',
      timestamp: '2025-10-02T14:32:45.432389',
      isBlock: false,
      isCurrent: false,
      isLastStep: false,
      stepId: null,
      blocks: [],
      metadata: null,
    },
    {
      messageId: '688e231e-840b-47df-884c-8fe37bde371c',
      role: 'ASSISTANT',
      text: '',
      timestamp: '2025-10-02T14:32:46.074390',
      isBlock: true,
      isCurrent: true,
      isLastStep: true,
      stepId: '32fcdf8c-3eac-4a9d-bfae-cf1ca6c37765',
      blocks: [
        {
          text: '¡Vamos bien! Para ayudarte a organizar mejor el contenido, necesito saber cuánto tiempo te gustaría que dure el curso.',
          type: 'markdown',
        },
        {
          text: '*¿Duración estimada del curso?*',
          type: 'markdown',
        },
        {
          name: 'course-length',
          type: 'radio-group',
          value: 'length-3',
          options: [
            {
              id: 'length-1',
              label: '*Corto*',
            },
            {
              id: 'length-2',
              label: '*Medio*',
            },
            {
              id: 'length-3',
              label: '*Largo*',
            },
            {
              id: 'skip',
              label: '*Saltar por ahora*',
            },
          ],
          required: true,
        },
        {
          type: 'divider',
        },
        {
          text: 'Esto solo es una guía inicial, no te preocupes si luego cambia.',
          type: 'markdown',
        },
      ],
      metadata: {},
    },
    {
      messageId: '0b3ea494-fe65-489e-89a2-4e0e42116771',
      role: 'USER',
      text: '[{"type": "markdown", "text": "*Largo*"}]',
      timestamp: '2025-10-02T14:32:51.141758',
      isBlock: false,
      isCurrent: false,
      isLastStep: false,
      stepId: null,
      blocks: [],
      metadata: null,
    },
  ] as any,
  summary: courseSummaryMock,
};
